import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { ArrowLeft, Calendar, Tag, ArrowRight } from "lucide-react";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";
const coverUrl = (v) => (v ? (/^https?:\/\//i.test(v) ? v : `${BACKEND_BASE}${v}`) : null);

// SEO-friendly single article page. Sets <title> and <meta description> dynamically
// so Google indexes each guide independently.
const BlogArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        setArticle(data);
        document.title = `${data.title} | Saturn Styling`;
        const desc = data.meta_description || data.excerpt || "";
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'description');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', desc);
        if (data.meta_keywords) {
          let mk = document.querySelector('meta[name="keywords"]');
          if (!mk) {
            mk = document.createElement('meta');
            mk.setAttribute('name', 'keywords');
            document.head.appendChild(mk);
          }
          mk.setAttribute('content', data.meta_keywords);
        }
        // Load related articles (all others)
        try {
          const rel = await axios.get(`${API}/blog`);
          setRelated((rel.data.articles || []).filter(a => a.slug !== slug).slice(0, 3));
        } catch (_) { /* ignore */ }

        // Inject Article + BreadcrumbList JSON-LD for Google Rich Results
        const canonical = `https://saturnstyling.ie/blog/${data.slug}`;
        const imgUrl = data.cover_image
          ? (/^https?:\/\//i.test(data.cover_image) ? data.cover_image : `https://saturnstyling.ie${data.cover_image}`)
          : `https://saturnstyling.ie/saturn-logo.png`;
        const galleryImages = Array.isArray(data.gallery)
          ? data.gallery.map(g => (/^https?:\/\//i.test(g.url) ? g.url : `https://saturnstyling.ie${g.url}`))
          : [];
        const articleSchema = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.title,
          "description": desc,
          "image": [imgUrl, ...galleryImages],
          "datePublished": data.published_at || data.created_at,
          "dateModified": data.updated_at || data.published_at || data.created_at,
          "author": { "@type": "Organization", "name": "Saturn Styling" },
          "publisher": {
            "@type": "Organization",
            "name": "Saturn Styling",
            "logo": { "@type": "ImageObject", "url": "https://saturnstyling.ie/saturn-logo.png", "width": 512, "height": 512 }
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
          "keywords": data.meta_keywords || (data.tags || []).join(", "),
        };
        const breadcrumb = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saturnstyling.ie/" },
            { "@type": "ListItem", "position": 2, "name": "Guides", "item": "https://saturnstyling.ie/blog" },
            { "@type": "ListItem", "position": 3, "name": data.title, "item": canonical },
          ],
        };
        const upsert = (id, obj) => {
          let el = document.getElementById(id);
          if (!el) {
            el = document.createElement('script');
            el.type = 'application/ld+json';
            el.id = id;
            document.head.appendChild(el);
          }
          el.textContent = JSON.stringify(obj);
        };
        upsert('ld-article', articleSchema);
        upsert('ld-breadcrumb', breadcrumb);

        // Canonical link
        let can = document.querySelector('link[rel="canonical"]');
        if (!can) {
          can = document.createElement('link');
          can.setAttribute('rel', 'canonical');
          document.head.appendChild(can);
        }
        can.setAttribute('href', canonical);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        console.error("Failed to load article", e);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo({ top: 0 });
    return () => {
      // Clean up JSON-LD on unmount so it doesn't stick around on other pages
      ['ld-article', 'ld-breadcrumb'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-saturn-black text-white flex items-center justify-center" data-testid="blog-article-loading">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-saturn-black text-white flex items-center justify-center p-6" data-testid="blog-article-notfound">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Article not found</h1>
          <p className="text-slate-400 mb-6">The guide you&apos;re looking for doesn&apos;t exist or hasn&apos;t been published yet.</p>
          <button onClick={() => navigate('/blog')} className="text-orange-400 hover:text-orange-300 font-semibold" data-testid="blog-article-back-to-list">← Back to all guides</button>
        </div>
      </div>
    );
  }
  if (!article) return null;

  return (
    <div className="min-h-screen bg-saturn-black text-white" data-testid={`blog-article-${article.slug}`}>
      {/* Article header */}
      <div className="border-b border-slate-800/70 bg-gradient-to-b from-orange-950/20 to-transparent">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <Link to="/blog" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm mb-8" data-testid="blog-article-back">
            <ArrowLeft className="w-4 h-4" /> All Guides
          </Link>
          {coverUrl(article.cover_image) && (
            <div className="mb-8 rounded-2xl overflow-hidden border border-slate-800 shadow-lg" data-testid="blog-article-cover">
              <img src={coverUrl(article.cover_image)} alt={article.title} className="w-full h-auto object-cover aspect-[16/9]" />
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-5">
            {(article.tags || []).map(t => (
              <span key={t} className="inline-flex items-center gap-1 text-xs text-orange-300 bg-orange-500/10 border border-orange-500/20 px-2.5 py-1 rounded">
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4">{article.title}</h1>
          {article.excerpt && <p className="text-slate-400 text-lg mt-3">{article.excerpt}</p>}
          {article.published_at && (
            <p className="text-slate-500 text-xs mt-6 inline-flex items-center gap-2">
              <Calendar className="w-3 h-3" /> Published {new Date(article.published_at).toLocaleDateString('en-IE', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>
      </div>

      {/* Article body */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <article
          className="blog-content text-slate-200 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.body_html }}
          data-testid="blog-article-body"
        />

        {/* Photo Gallery */}
        {Array.isArray(article.gallery) && article.gallery.length > 0 && (
          <div className="mt-14" data-testid="blog-article-gallery">
            <h2 className="text-2xl font-bold mb-6 text-orange-400">Photo Gallery</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              {article.gallery.map((g, i) => (
                <figure
                  key={g.image_id || g.url || i}
                  className="rounded-xl overflow-hidden border border-slate-800 bg-slate-900/50"
                  data-testid={`blog-article-gallery-item-${i}`}
                >
                  <img
                    src={coverUrl(g.url)}
                    alt={g.alt || article.title}
                    loading="lazy"
                    className="w-full h-auto aspect-[4/3] object-cover"
                  />
                  {g.caption && (
                    <figcaption className="p-4 text-sm text-slate-400">{g.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 p-8 bg-gradient-to-br from-orange-500/15 to-slate-900/60 border border-orange-500/40 rounded-2xl text-center">
          <h3 className="text-2xl font-bold mb-2">Ready to book?</h3>
          <p className="text-slate-300 mb-5">Get an instant quote and secure your slot online in under 60 seconds.</p>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-lg transition-colors"
            data-testid="blog-article-book-cta"
          >
            Book Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && (
        <div className="border-t border-slate-800/70 bg-slate-950/40">
          <div className="max-w-5xl mx-auto px-6 py-14">
            <h3 className="text-xl font-bold mb-6">Keep Reading</h3>
            <div className="grid gap-6 sm:grid-cols-3">
              {related.map(r => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="block bg-slate-900/60 border border-slate-800 hover:border-orange-500/50 rounded-xl p-5 transition-colors"
                  data-testid={`blog-related-${r.slug}`}
                >
                  <h4 className="font-semibold text-white group-hover:text-orange-300 mb-2 line-clamp-2">{r.title}</h4>
                  <p className="text-sm text-slate-400 line-clamp-2">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Article prose styling */}
      <style>{`
        .blog-content h2 { color: #f97316; font-size: 1.6rem; font-weight: 800; margin: 2rem 0 1rem; line-height: 1.3; }
        .blog-content h3 { color: #fdba74; font-size: 1.25rem; font-weight: 700; margin: 1.5rem 0 0.75rem; }
        .blog-content p { color: #cbd5e1; font-size: 1.05rem; margin: 1rem 0; }
        .blog-content ul { list-style: disc; padding-left: 1.5rem; margin: 1rem 0; color: #cbd5e1; }
        .blog-content ul li { margin: 0.5rem 0; }
        .blog-content strong { color: #fff; }
        .blog-content a { color: #f97316; text-decoration: underline; }
        .blog-content a:hover { color: #fb923c; }
      `}</style>
    </div>
  );
};

export default BlogArticlePage;
